import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { panelTo } from './panelLink';
import { chipsFor, cn, isNewRequestFor } from './model/derive';
import { formatDue } from './model/time';
import { dueInfo, useWorkboardCtx } from './model/useWorkboard';
import { StatusChip, UnreadDot } from './primitives';
import { HandoffFlow } from './PersonChip';

/** "You" — 나와 관련된 태스크 표시 (All tasks) */
export const YouLabel = ({ className }) => (
  <span
    className={cn(
      'inline-flex h-[18px] shrink-0 items-center rounded-full bg-green-deep02 px-1.5 text-[11px] font-medium leading-none text-grayscale-gray00',
      className,
    )}
  >
    You
  </span>
);

/**
 * 보드 · 스윔레인의 태스크 카드 — 상태 칩 · 제목 · 담당자 → 받는 사람 · 마감.
 * @param you     나와 관련된 카드 — green 테두리 + You
 * @param labels  상태 이름 바꿔 부르기 (My tasks 의 Changes requested)
 */
const TaskCard = ({ task, you = false, labels = null }) => {
  const ctx = useWorkboardCtx();
  const { adjusted } = dueInfo(ctx, task);
  const isNew = isNewRequestFor(task, ctx.data.viewerId);
  // 누르면 보드 위에 태스크 패널 (전체 페이지는 패널의 Open full page)
  const location = useLocation();

  return (
    <Link
      to={panelTo(location, task.id)}
      data-coach='task-card'
      className={cn(
        // hover — 제자리에서 그림자만 진해진다 (위로 뜨면 스윔레인 칸의 overflow:hidden 에 테두리가 잘린다).
        // 진한 그림자도 shadow-4 와 같은 범위(y 2 · blur 8)라 칸 여백 8px 안에 들어간다
        'relative flex flex-col gap-2.5 rounded-[10px] border bg-grayscale-gray00 p-3 shadow-4 transition-shadow duration-200 hover:shadow-[0_1px_2px_rgba(121,111,98,0.16),0_2px_8px_rgba(121,111,98,0.28)]',
        you ? 'border-green-deep02' : 'border-transparent',
      )}
    >
      {/* 내 수락을 기다리는 배정 — 수락 대기 시간이 흐르고 있다 */}
      {isNew && (
        <span
          aria-hidden
          className='absolute -left-px bottom-2.5 top-2.5 w-[3px] rounded-r-[3px] bg-green-deep02'
        />
      )}
      <div className='flex items-start gap-1.5'>
        <div className='flex min-w-0 flex-1 flex-wrap items-center gap-1.5'>
          {isNew && !task.openedAt && <UnreadDot />}
          {chipsFor(task, ctx.data.viewerId, ctx, labels).map((c) => (
            <StatusChip key={c.label} chip={c} />
          ))}
        </div>
        {you && <YouLabel className='mt-0.5' />}
      </div>
      <span className='text-[14px] font-medium leading-[140%] text-text-primary [text-wrap:pretty]'>
        {task.title}
      </span>
      <div className='flex flex-wrap items-center justify-between gap-x-2 gap-y-1.5 text-[12px] leading-[140%]'>
        <HandoffFlow
          assignee={ctx.memberById.get(task.assigneeId)}
          receiver={ctx.memberById.get(task.receiverId)}
        />
        {adjusted ? (
          <span
            className='whitespace-nowrap font-medium text-functional-warning04'
            title={`Original ${formatDue(task.dueAt, ctx.displayTz)} — moved because an earlier hand-off was late`}
          >
            Adj. {formatDue(adjusted, ctx.displayTz)}
          </span>
        ) : (
          <span className='whitespace-nowrap text-text-secondary'>
            {formatDue(task.dueAt, ctx.displayTz)}
          </span>
        )}
      </div>
    </Link>
  );
};

export default TaskCard;
